import { CfJson } from '@metservicedev/platforms-services-interface/src/model/CfJson';
import cloneDeep from 'lodash/cloneDeep';
import moment, { Moment } from 'moment-timezone';

const NONE_DATA_VARIABLES = ['longitude', 'latitude', 'time', 'lev', 'ensemble_number', 'station'];
const getDataVariables = (netcdfjson: CfJson) => Object.keys(netcdfjson.variables).filter((variableKey) => !NONE_DATA_VARIABLES.includes(variableKey));

const convertNetcdfjsonToLineByLineDatum = ({ netcdfjson }: { netcdfjson?: CfJson | null }) => {
    if (!netcdfjson) {
        return;
    }

    const clonedJson = cloneDeep(netcdfjson);
    const variables = getDataVariables(clonedJson);

    // The response should be array, but Regrid returns value instead of array of length 1
    const timeDatums: string[] =
        !Array.isArray(clonedJson.variables.time.data) && !!clonedJson.variables.time.data ? [clonedJson.variables.time.data] : clonedJson.variables.time.data;

    const massagedData: { [key: string]: unknown; time: Moment }[] = timeDatums.map((time) => ({ time: moment.utc(time) }));

    variables.forEach((variable) => {
        // The response should be array, but Regrid returns value instead of array of length 1
        const variableDatums: unknown[] =
            !Array.isArray(clonedJson.variables[variable].data) && !!clonedJson.variables[variable].data
                ? [clonedJson.variables[variable].data]
                : clonedJson.variables[variable].data;

        if (variableDatums) {
            variableDatums.forEach((datum, key) => {
                const standard_name =
                    clonedJson.variables[variable].attributes && clonedJson.variables[variable].attributes.standard_name
                        ? (clonedJson.variables[variable].attributes.standard_name as string)
                        : variable;

                const fillValue = clonedJson.variables[variable].attributes._FillValue ?? null;
                if (datum === fillValue) {
                    massagedData[key][standard_name] = null;
                } else {
                    massagedData[key][standard_name] = datum;
                }
            });
        }
    });

    return massagedData;
};

const convertNetcdfjsonToLineByLineDatumsWithoutMoment = ({ netcdfjson }: { netcdfjson?: CfJson | null }) => {
    if (!netcdfjson) {
        return;
    }
    const dataVariables = getDataVariables(netcdfjson);

    const unflattenedData = dataVariables.map((variableName) => {
        const variableData = netcdfjson.variables[variableName].data;
        const variableShape = Object.entries(netcdfjson.dimensions).map(([key]) => key);

        const variableDataFlat = variableData.flat();

        const dimensionsArrayUnFlat = variableShape.map((dimension) => {
            return netcdfjson.variables[dimension].data.map((dimensionalValue: string | number) => {
                return { [dimension]: dimensionalValue };
            });
        });

        const dimensionsArray = dimensionsArrayUnFlat.flatMap((dimension, index, arr) => {
            return dimension.flatMap((dimensionalValue: { [x: string]: string | number }) => {
                if (dimensionsArrayUnFlat.length === 1) {
                    return dimensionalValue;
                }
                if (index === dimensionsArrayUnFlat.length - 1) {
                    return [];
                }
                return arr[index + 1].map((dimension: { [x: string]: string | number }) => {
                    return { ...dimensionalValue, ...dimension };
                });
            });
        });

        return variableDataFlat.map((variable: string | number, index: number) => {
            const fillValue = netcdfjson.variables[variableName].attributes._FillValue === variable ? null : variable;
            return { [variableName]: fillValue, ...dimensionsArray[index] };
        });
    });

    if (unflattenedData.length === 1) return unflattenedData[0];

    return unflattenedData[0]?.map((datum: { [x: string]: string | number }, i: number) => {
        return unflattenedData.reduce((acc, curr) => {
            return { ...acc, ...curr[i] };
        }, datum[0]);
    });
};

export default convertNetcdfjsonToLineByLineDatum;
export { convertNetcdfjsonToLineByLineDatumsWithoutMoment };
